import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Button} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import {useDispatch, useSelector} from 'react-redux';
import {StyledExpenseDetailsPanel} from './StyledExpenseDetailsPanel';
import DeleteModal from '../../../DeleteModal';
import Tabs from '../../../Tabs';
import ApprovalsTab from '../ExpenseDetailsPanelTabs/ApprovalsTab';
import ExpenseDetailsTab from '../ExpenseDetailsPanelTabs/ExpenseDetailsTab';
import ExpenseDetailsTabFooter from '../ExpenseDetailsPanelTabs/ExpenseDetailsTabFooter';
import {expensesHelpers} from '../../../../utils/helpers';
import {DEFAULT_MODAL_PROPS} from '../../../../constants';
import {useIsAdvancedExpensesPlan} from '../../../../hooks';
import {expenseActions} from '../../../../state/actions/expense.actions';

const tabKeys = {
  APPROVALS: 'approvals',
  DETAILS: 'details',
};

const {APPROVALS, DETAILS} = tabKeys;

const defaultTabKey = tabKeys.DETAILS;

const ExpenseDetailsPanel = ({
  defaultActivePanelKey = [],
  expense,
  loading,
  onApprove,
  onClose,
  onDelete,
  onDiscardAutoApproval,
  onEdit,
  onInvoiceRemove,
  onReassign,
  onReject,
  onReviewEdit,
  onSubscriptionUpdate,
  onUpdate,
  subscription = null,
  tabsProps,
  transaction = null,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'expenses']);
  const [activeTabKey, setActiveTabKey] = useState(defaultTabKey);
  const [deleteModalProps, setDeleteModalProps] = useState(DEFAULT_MODAL_PROPS);

  const dispatch = useDispatch();

  const trans = (key, props) => t(`expenses:${key}`, props);

  const getLabel = (key) => trans(`expenseDetails.tabs.${key}`);

  const { isAdmin, employeeEmail } = useSelector(({ user }) => ({
    employeeEmail: user?.employee?.email,
    isAdmin: user.isAdmin,
  }));

  const isAdvancedExpensesPlan = useIsAdvancedExpensesPlan();

  const enableFooter = useMemo(() => {
    if (!isAdvancedExpensesPlan) return true;

    const isExpenseReportedByEmployee = expense?.reported_by?.email === employeeEmail;
    const isDraft = expensesHelpers.isExpenseDraft(expense);
    return !(isDraft && isExpenseReportedByEmployee);
  }, [employeeEmail, expense, isAdvancedExpensesPlan]);

  const hasFooter = useMemo(() => {
    if (!enableFooter) return false;

    const isDraftExpense = expensesHelpers.isExpenseDraft(expense);
    if (isDraftExpense) return true;
    return expensesHelpers.isAvailableApproveActions({employeeEmail, expense, isAdmin});
  }, [employeeEmail, expense?.review_status, expense?.reported_by?.email, isAdmin, enableFooter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTabKey !== defaultTabKey) {
      setActiveTabKey(defaultTabKey);
    }
  }, [expense?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenDeleteModal = () => setDeleteModalProps({...deleteModalProps, open: true});

  const handleCancelDeleteModal = () => setDeleteModalProps(DEFAULT_MODAL_PROPS);

  const handleOkDeleteModal = () => {
    const id = expense?.id;
    if (!id) return;

    setDeleteModalProps({...deleteModalProps, loading: true});

    dispatch(expenseActions.deleteExpense({
      id,
      successCallback: () => {
        handleCancelDeleteModal();
        onDelete?.(expense);
      },
      errorCallback: (response) => {
        expensesHelpers.handleExpenseError({response, fallbackKey: 'deleteExpenseError', t});
        setDeleteModalProps({...deleteModalProps, loading: false});
      },
    }));
  }

  const handleSubmitExpense = ({e, callbackFunc}) => {
    e.stopPropagation();

    const id = expense?.id;
    if (!id) return;

    dispatch(expenseActions.submitExpenseForReview({
      id,
      successCallback: (response) => {
        callbackFunc?.();
        onUpdate?.(response);
      },
      errorCallback: (response) => {
        expensesHelpers.handleExpenseError({response, fallbackKey: 'submitExpenseError', t});
        callbackFunc?.(response);
      },
    }));
  }

  const inReviewMode = useMemo(() => expensesHelpers.isExpenseInReviewOrDraft(expense), [expense?.review_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    {
      key: DETAILS,
      label: getLabel(DETAILS),
      children: (
        <ExpenseDetailsTab
          className={`expense-details-tab ${hasFooter ? 'with-footer' : 'without-footer'}`}
          defaultActivePanelKey={defaultActivePanelKey}
          expense={expense}
          onEdit={onEdit}
          onInvoiceRemove={onInvoiceRemove}
          onSubscriptionUpdate={onSubscriptionUpdate}
          onUpdate={onUpdate}
          subscription={subscription}
          transaction={transaction}
        />
      ),
    },
    {
      key: APPROVALS,
      label: getLabel(APPROVALS),
      children: (
        <ApprovalsTab
          expense={expense}
          entity={expense}
          inReviewMode={inReviewMode}
          onApprove={onApprove}
          onDiscardAutoApproval={onDiscardAutoApproval}
          onReassign={onReassign}
          onReject={onReject}
          onReviewEdit={onReviewEdit}
        />
      ),
    },
  ];

  return (
    <StyledExpenseDetailsPanel {...rest}>
      <Tabs
        activeKey={activeTabKey}
        items={tabs}
        onChange={setActiveTabKey}
        type='card'
        tabBarExtraContent={{
          right: <Button icon={<CloseOutlined />} type='text' onClick={onClose} />
        }}
        {...tabsProps}
      />

      {(enableFooter && activeTabKey === DETAILS) && (
        <ExpenseDetailsTabFooter
          expense={expense}
          onApprove={onApprove}
          onDelete={handleOpenDeleteModal}
          onReject={onReject}
          onSubmit={handleSubmitExpense}
        />
      )}

      <DeleteModal
        description={t(`expenses:modal.delete.description`)}
        onCancel={handleCancelDeleteModal}
        onOk={handleOkDeleteModal}
        title={t(`expenses:modal.delete.title`)}
        {...deleteModalProps}
      />
    </StyledExpenseDetailsPanel>
  );
}

ExpenseDetailsPanel.propTypes = {
  defaultActivePanelKey: PropTypes.array,
  expense: PropTypes.object,
  onApprove: PropTypes.func,
  onDelete: PropTypes.func,
  onDiscardAutoApproval: PropTypes.func,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onInvoiceRemove: PropTypes.func,
  onReassign: PropTypes.func,
  onReject: PropTypes.func,
  onReviewEdit: PropTypes.func,
  onUpdate: PropTypes.func,
  subscription: PropTypes.object,
  tabsProps: PropTypes.object,
  transaction: PropTypes.object
}

export default ExpenseDetailsPanel;
